/**
 * TAN-8655 — Contexto del título de página.
 *
 * Resuelve una sola vez, por cambio de ruta, las tres cosas que necesitan los dos escritores del
 * `document.title`: el nombre de la aplicación, el título estático de la ruta activa y si esa ruta
 * ha delegado el título en su página.
 *
 * Es **de solo lectura a propósito**: expone valores derivados y ningún setter. La alternativa
 * (que el contexto guardase con `useState` el segmento dinámico que aporta cada página) se
 * descartó porque es estado mutable compartido, y de ahí salen los tres modos de fallo clásicos
 * del patrón: dos escritores compitiendo, el segmento de la página anterior quedándose pegado al
 * navegar, y re-renders en cascada de todos los consumidores. Aquí quién escribe se decide con un
 * orden de efectos garantizado por el árbol —el handler escribe el genérico, la página lo refina—,
 * así que no hay nada que ordenar ni que limpiar.
 */

import React, { createContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { resolveRoute } from 'app/router/pageTitleUtils';
import { logToSentry } from 'core/utils/sentry';
import { TITLE_KEYS } from 'app/router/routeTitleKeys';

import type { RouteConfig } from 'app/types/router';

export type PageTitleContextValue = {
  /** Nombre de la aplicación (scope `name` del tenant, o la constante de Edelvives). */
  appName?: string;
  /** Título estático y traducido de la ruta activa. */
  routeTitle: string;
};

export const PageTitleContext = createContext<PageTitleContextValue>({
  appName: undefined,
  routeTitle: '',
});

type PageTitleProviderProps = {
  appName?: string;
  children: React.ReactNode;
  /** Regla de exactitud del `renderRoutes` del router que provee. Ver `resolveRoute`. */
  isExact: (route: RouteConfig) => boolean;
  /** Rutas del router en el mismo orden en el que se pintan. */
  routes: RouteConfig[];
};

function PageTitleProvider({ appName, children, isExact, routes }: PageTitleProviderProps): JSX.Element {
  const location = useLocation();
  const { t } = useTranslation();

  const value = useMemo<PageTitleContextValue>(() => {
    // Este provider envuelve el árbol entero de la aplicación, y `ChunkLoadErrorBoundary` relanza
    // cualquier error que no sea de carga de chunk: si algo aquí lanzara, React desmontaría el árbol y
    // la app se quedaría en blanco. Un título es cosmético y jamás debe poder provocar eso, así que
    // cualquier fallo (un `path` inválido que haga saltar a `matchPath`, un bundle de i18n
    // corrupto) degrada a "sin título" y deja la pantalla intacta.
    try {
      const matchedRoute = resolveRoute(location?.pathname, routes, isExact);
      // Sin match lo que se pinta es el `<Route path="*" component={NotFound} />` final, así que el
      // título tiene que decir lo mismo.
      const titleKey = matchedRoute?.titleKey ?? TITLE_KEYS.NotFound;

      return {
        appName,
        routeTitle: t(titleKey),
      };
    } catch (error) {
      logToSentry('warning', 'No se pudo resolver el título de página', { error: error instanceof Error ? error : new Error(String(error)) });

      return { appName, routeTitle: '' };
    }
    // `t` está en las dependencias a propósito: react-i18next le cambia la identidad en
    // `languageChanged`, y es lo que hace que el título se recomponga al cambiar de idioma.
  }, [appName, isExact, location?.pathname, routes, t]);

  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

export default PageTitleProvider;
